/**
 * Tabs Component
 *
 * Note: Tabs is a special container component that auto-generates tab-item children.
 * The parser.ts handles the creation of tab-item _Body entries.
 */

import { IBuilderComponent } from '../../base'
import {
  deepMerge,
  parseCornerRadiusFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatCornerRadiusFormat,
  formatOrientationFormat,
  formatSpacingFormat,
  formatWidthFormat,
  formatResponsiveValue,
} from '../../format-utils'
import type {
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
} from '../../types'
import type { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder'

import type { AiComponentInput } from '../../registry'
import tabsTemplate from './tabs.template.json'

export interface ITabsComponentSettings {
  // Data settings
  'tabs-data-show-icon': boolean
  'tabs-data-show-label': boolean

  // Design settings
  'tabs-design-align': ResponsiveValue<any>
  'tabs-design-content-align': ResponsiveValue<any>
  'tabs-design-bg-color': string
  'tabs-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'tabs-design-shape': string
  'tabs-design-orientation': ResponsiveValue<string>
  'tabs-design-content-shadow': any
  'tabs-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'tabs-design-padding': ResponsiveValue<SpacingInputValue>
  'tabs-design-item-padding': ResponsiveValue<SpacingInputValue>
  'tabs-design-content-padding': ResponsiveValue<SpacingInputValue>
  'tabs-design-variant': string
  'tabs-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

export interface ITabsComponent extends IBuilderComponent {
  componentId: 'tabs'
  groupId: 'container'
  settings: ITabsComponentSettings
}

/**
 * Tab item definition
 */
export interface AiTabItem {
  title: string
  icon?: string
  children?: AiComponentInput[] // Components inside this tab
}

/**
 * Tabs AI Input
 */
export interface AiTabsInput {
  tabs: AiTabItem[] // Array of tab definitions
  showIcon?: boolean // Show tab icons (default: true)
  showLabel?: boolean // Show tab labels (default: true)
  orientation?: string // Format: "horizontal" or "vertical" or "horizontal ta:vertical"
  variant?: string // e.g., "default", "underline", "pills"
  width?: string // Format: "w:100%" or "w:50%" or "w:100% de:w:50%"
  margin?: string // Format: "m:16px"
  padding?: string // Format: "p:4px"
  itemPadding?: string // Padding for each tab item
  contentPadding?: string // Padding for tab content area
  backgroundColor?: string // Background color for tabs bar
  cornerRadius?: string // Corner radius
  contentShadow?: string // Shadow for content area
  align?: string // Alignment of tabs container
  contentAlign?: string // Alignment of content inside tabs
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

const TABS_DEFAULT_TEMPLATE = tabsTemplate as any

export class TabsComp {
  getTemplate(): ITabsComponentSettings {
    // Extract just the tabs settings from the template
    const templateSettings = TABS_DEFAULT_TEMPLATE._BodySettings?.[TABS_DEFAULT_TEMPLATE._Body?.[0]?._id]
    return templateSettings || {}
  }

  /**
   * format tabs component settings to AI tabs input
   * Note: This only formats the tabs container settings.
   * The tab-item children are handled by the special formatter.
   */
  format(
    settings: ITabsComponentSettings,
    body: IBody,
    bodySettings: IBodySettings,
    formatChild: (child: IBody, settings: IBodySettings) => AiComponentInput
  ): AiTabsInput {
    const result: Partial<AiTabsInput> = {}

    // Data - Show icon/label
    if (settings['tabs-data-show-icon'] !== undefined) {
      result.showIcon = settings['tabs-data-show-icon']
    }

    if (settings['tabs-data-show-label'] !== undefined) {
      result.showLabel = settings['tabs-data-show-label']
    }

    // Design - Orientation
    const orientation = formatOrientationFormat(settings['tabs-design-orientation'])
    if (orientation) {
      result.orientation = orientation
    }

    // Design - Variant
    if (settings['tabs-design-variant']) {
      result.variant = settings['tabs-design-variant']
    }

    // Design - Width (handle OverflowSettings)
    const width = formatWidthFormat(settings['tabs-design-width'])
    if (width) {
      result.width = width
    }

    // Design - Margin (handle OverflowSettings)
    const margin = formatSpacingFormat(settings['tabs-design-margin'])
    if (margin) {
      result.margin = margin
    }

    // Design - Padding (tabs bar)
    const padding = formatSpacingFormat(settings['tabs-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Design - Item Padding
    const itemPadding = formatSpacingFormat(settings['tabs-design-item-padding'])
    if (itemPadding) {
      result.itemPadding = itemPadding
    }

    // Design - Content Padding
    const contentPadding = formatSpacingFormat(settings['tabs-design-content-padding'])
    if (contentPadding) {
      result.contentPadding = contentPadding
    }

    // Design - Background Color
    if (settings['tabs-design-bg-color']) {
      result.backgroundColor = settings['tabs-design-bg-color']
    }

    // Design - Corner Radius
    const cornerRadius = formatCornerRadiusFormat(settings['tabs-design-shape'])
    if (cornerRadius) {
      result.cornerRadius = cornerRadius
    }

    // Design - Content Shadow
    if (settings['tabs-design-content-shadow']) {
      result.contentShadow = settings['tabs-design-content-shadow']
    }

    // Design - Alignment
    const align = formatResponsiveValue(settings['tabs-design-align'], (val) => {
      if (val === 'parent') return 'h:parent'
      if (val === 'start') return 'h:start'
      if (val === 'end') return 'h:end'
      return 'h:center'
    })
    if (align) {
      result.align = align
    }

    // Design - Content Alignment
    const contentAlign = formatResponsiveValue(settings['tabs-design-content-align'], (val) => {
      if (val === 'start') return 'h:start'
      if (val === 'end') return 'h:end'
      return 'h:center'
    })
    if (contentAlign) {
      result.contentAlign = contentAlign
    }

    // Format tab-items from body children
    const tabs: AiTabItem[] = []
    if (body._Body && body._Body.length > 0) {
      for (const tabItemBody of body._Body) {
        if (tabItemBody._ComponentID === 'tab-item') {
          // Get tab-item settings (title and icon)
          const tabItemSettings = bodySettings[tabItemBody._id] as { title?: string; icon?: string } | undefined
          const tabItem: AiTabItem = {
            title: tabItemSettings?.title || tabItemBody._Title || '',
          }

          // Add icon if present
          if (tabItemSettings?.icon) {
            tabItem.icon = tabItemSettings.icon
          }

          // Recursively format tab-item children
          if (tabItemBody._Body && tabItemBody._Body.length > 0) {
            tabItem.children = tabItemBody._Body.map((child) => formatChild(child, bodySettings))
          }

          tabs.push(tabItem)
        }
      }
    }

    result.tabs = tabs

    return result as AiTabsInput
  }

  /**
   * parse AI tabs input to tabs component settings
   * Note: This only returns the tabs container settings.
   * The tab-item children are created by the parser.
   */
  parse(input: AiTabsInput): ITabsComponentSettings {
    const template = this.getTemplate()
    const result = deepMerge(template, {})

    // Data - Show icon/label
    if (input.showIcon !== undefined) {
      result['tabs-data-show-icon'] = input.showIcon
    }

    if (input.showLabel !== undefined) {
      result['tabs-data-show-label'] = input.showLabel
    }

    // Design - Orientation
    if (input.orientation) {
      result['tabs-design-orientation'] = parseResponsiveFormat(input.orientation, (v) => v)
    }

    // Design - Variant
    if (input.variant) {
      result['tabs-design-variant'] = input.variant
    }

    // Design - Width
    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['tabs-design-width'] = {
        ...widthParsed,
        overflowSetting: result['tabs-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['tabs-design-margin'] = {
        ...marginParsed,
        overflowSetting: result['tabs-design-margin'].overflowSetting,
      }
    }

    // Design - Padding (tabs bar)
    if (input.padding) {
      result['tabs-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Item Padding
    if (input.itemPadding) {
      result['tabs-design-item-padding'] = parseSpacingFormat(input.itemPadding)
    }

    // Design - Content Padding
    if (input.contentPadding) {
      result['tabs-design-content-padding'] = parseSpacingFormat(input.contentPadding)
    }

    // Design - Background Color
    if (input.backgroundColor) {
      result['tabs-design-bg-color'] = input.backgroundColor
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      result['tabs-design-shape'] = parseCornerRadiusFormat(input.cornerRadius).phone
    }

    // Design - Content Shadow
    if (input.contentShadow) {
      result['tabs-design-content-shadow'] = input.contentShadow
    }

    // Design - Alignment
    if (input.align) {
      result['tabs-design-align'] = parseResponsiveFormat(input.align, (v) => v)
    }

    // Design - Content Alignment
    if (input.contentAlign) {
      result['tabs-design-content-align'] = parseResponsiveFormat(input.contentAlign, (v) => v)
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['tabs-design-hover-effect'] = input.hoverEffect
    }

    return result
  }
}

